import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import { B, H1, P } from '../../../components/Typography';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import accessibilityTags from '../../../../accessibilityTags';
import { scheduleOnRN } from 'react-native-worklets';

const SLIDER_WIDTH = 31;

const IconSliderView = styled.View<{ grayedOut?: boolean }>`
  position: absolute;
  width: ${SLIDER_WIDTH}px;
  height: ${SLIDER_WIDTH}px;
  background: white;
  border: 5px solid ${p => (p.grayedOut ? '#9A9A9A' : '#8577f6')};
  border-radius: ${SLIDER_WIDTH / 2}px;
`;

const StyledSliderContainer = styled.View`
  width: 100%;
  height: 80px;
  align-self: center;
  justify-content: center;
`;

const StyledSliderTrackView = styled.View`
  position: absolute;
  margin-top: 4px;
  padding-vertical: 2px;
  padding-horizontal: 4px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  start: 0;
  end: 0;
`;

const StyledSliderTrackLineView = styled.View`
  background-color: #edecf5;
  border-radius: 10px;
  height: 11.15px;
  width: 100%;
  margin-horizontal: -15px;
  margin-bottom: 0.2px;
`;

const StyledSliderValue = styled.View`
  align-items: center;
  margin-top: 2px;
`;

const StyledSliderValueFont = styled(H1)`
  font-size: 52px;
  line-height: 62px;
`;

const StyledMinMaxFont = styled(B)`
  font-size: 20px;
  line-height: 23px;
`;

const StyledSliderLabelsContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  margin-top: -25px;
  gap: 32px;
`;

const StyledSliderColumnView = styled.View`
  flex: 1 1 auto;
  max-width: 50%;
`;

const StyledSliderLabelView = styled.View`
  margin-top: 4px;
`;

const StyledSliderLabelText = styled(P)`
  padding-top: 4px;
`;

const StyledThumbView = styled.View`
  height: 26px;
  width: ${SLIDER_WIDTH}px;
  background-color: transparent;
`;

const StyledBar = styled(Animated.View)`
  height: 11.15px;
  border-radius: 10px;
  position: absolute;
  left: 0;
  background-color: #8577f7;
`;

export type FormSliderConfig = {
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  bars?: boolean;
  minValueDescription?: string;
  maxValueDescription?: string;
  orientation?: 'horizontal' | 'vertical';
  valueLabelDisplay?: 'on' | 'auto' | 'off';
};

const FormSlider = <T extends object>({
  element
}: FormElementProps<T, FormSliderConfig>) => {
  const { formValues, setFormValue } = useDynamicFormContext<
    T,
    FormSliderConfig
  >();
  const key = (element.dataSource as keyof T) || element.key;
  const {
    defaultValue,
    min = 0,
    max = 100,
    step = 1,
    bars = true,
    minValueDescription,
    maxValueDescription
  } = element.config || {};

  const offsetX = useSharedValue(0);
  const translateX = useSharedValue(0);
  const [sliderDimensions, setSliderDimensions] = useState({
    left: 0,
    right: 0
  });
  const [sliderValue, setSliderValue] = useState<number | undefined>(
    formValues[key] != undefined
      ? isNaN(formValues[key] as any)
        ? (defaultValue ?? min)
        : +(formValues[key] as any)
      : defaultValue
  );
  const sliderTotalSteps = (max - min) / step;
  const previousValueRef = useRef<number | undefined>(undefined);
  const commitSliderValue = useCallback(
    (value: any) => {
      setFormValue(key, value, element);
    },
    [key, element]
  );

  const handleSliderState = useCallback(
    (translationX: number, fromTapEvent?: boolean) => {
      'worklet';
      const validTranslationX = sliderDimensions.right / sliderTotalSteps;
      let progress = Math.round(translationX / validTranslationX);

      const newTranslationX = progress * validTranslationX;
      if (
        newTranslationX !== 0 &&
        newTranslationX > sliderDimensions.left &&
        newTranslationX < sliderDimensions.right
      ) {
        translateX.value = progress * validTranslationX;
      } else if (newTranslationX <= 0) {
        translateX.value = sliderDimensions.left;
      } else if (newTranslationX >= sliderDimensions.right) {
        translateX.value = sliderDimensions.right;
      } else if (sliderValue == undefined || isNaN(sliderValue)) {
        translateX.value = (sliderTotalSteps / 2) * validTranslationX;
      }

      progress = progress * step;
      let value = min;
      if (progress < min) {
        value = min;
      } else if (progress >= max) {
        value = max;
      } else {
        value = progress + min;
      }

      if (value === sliderValue) return;
      scheduleOnRN(setSliderValue, value);
      if (fromTapEvent) {
        scheduleOnRN(commitSliderValue, value);
      }
    },
    [
      element,
      key,
      max,
      min,
      commitSliderValue,
      sliderDimensions.left,
      sliderDimensions.right,
      sliderTotalSteps,
      sliderValue,
      step,
      translateX
    ]
  );

  useEffect(() => {
    if (sliderDimensions.right === 0) return;
    const formValue =
      formValues[key] != undefined ? formValues[key] : undefined;
    const sliderInitialValue =
      formValue != undefined &&
      !isNaN(+formValue) &&
      previousValueRef.current === undefined
        ? (formValues[key] as number)
        : sliderValue;

    previousValueRef.current = sliderInitialValue;
    const validTranslationX = sliderDimensions.right / sliderTotalSteps;
    handleSliderState(
      ((sliderInitialValue as any) - min) * (1 / step) * validTranslationX
    );
  }, [
    formValues,
    handleSliderState,
    key,
    min,
    sliderDimensions,
    sliderTotalSteps,
    sliderValue,
    step
  ]);

  const panGesture = Gesture.Pan()
    .onStart(() => {
      offsetX.value = translateX.value;
    })
    .onUpdate(event => {
      const translationX = event.translationX + offsetX.value;
      handleSliderState(translationX);
    })
    .onFinalize((_, success) => {
      if (success) scheduleOnRN(commitSliderValue, sliderValue);
    });

  const tapGesture = Gesture.Tap().onFinalize(event => {
    handleSliderState(event.x - SLIDER_WIDTH / 2, true);
  });

  const currentValue = formValues[key];
  const handlePress = () => {
    if (defaultValue != null && currentValue == null) {
      setFormValue(key, defaultValue as any, element);
    }
  };

  const sliderThumbAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }]
    };
  });

  const sliderTrackAnimatedStyle = useAnimatedStyle(() => {
    return {
      width:
        sliderValue == undefined || isNaN(sliderValue)
          ? 0
          : translateX.value + SLIDER_WIDTH / 2
    };
  });

  return (
    <View>
      <FormElementQuestion element={element} />
      <StyledSliderValue>
        <StyledSliderValueFont color='dark'>
          {sliderValue || sliderValue === 0 ? sliderValue : '--'}
        </StyledSliderValueFont>
      </StyledSliderValue>
      <Pressable onPress={handlePress}>
        <StyledSliderContainer
          onLayout={event => {
            const { width } = event.nativeEvent.layout;
            setSliderDimensions({
              left: 0,
              right: width - SLIDER_WIDTH
            });
          }}
        >
          <GestureDetector gesture={tapGesture}>
            <StyledSliderTrackView>
              <StyledSliderTrackLineView />
              {bars && (
                <StyledBar
                  style={sliderTrackAnimatedStyle}
                  accessibilityLabel={accessibilityTags.sliderID}
                  testID={accessibilityTags.sliderID}
                />
              )}
            </StyledSliderTrackView>
          </GestureDetector>
          <GestureDetector gesture={panGesture}>
            <Animated.View
              style={[{ width: SLIDER_WIDTH }, sliderThumbAnimatedStyle]}
            >
              <IconSliderView
                grayedOut={sliderValue == undefined || isNaN(sliderValue)}
              />
              <StyledThumbView />
            </Animated.View>
          </GestureDetector>
        </StyledSliderContainer>
      </Pressable>
      <StyledSliderLabelsContainer>
        <StyledSliderColumnView>
          <StyledMinMaxFont color='dark'>{min}</StyledMinMaxFont>
          {Boolean(minValueDescription) && (
            <StyledSliderLabelView>
              <StyledSliderLabelText color='dark'>
                {minValueDescription}
              </StyledSliderLabelText>
            </StyledSliderLabelView>
          )}
        </StyledSliderColumnView>
        <StyledSliderColumnView>
          <StyledMinMaxFont color='dark' style={{ textAlign: 'right' }}>
            {max}
          </StyledMinMaxFont>
          {Boolean(maxValueDescription) && (
            <StyledSliderLabelView>
              <StyledSliderLabelText
                color='dark'
                style={{ textAlign: 'right' }}
              >
                {maxValueDescription}
              </StyledSliderLabelText>
            </StyledSliderLabelView>
          )}
        </StyledSliderColumnView>
      </StyledSliderLabelsContainer>
    </View>
  );
};

export const FormSliderPlugin: FormElementPlugin<any, FormSliderConfig> = {
  name: 'Slider',
  type: 'Slider',
  dataTypes: ['text', 'integer', 'numeric'],
  element: FormSlider
};
